'use client';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import type {
  CreateShippingAddressRequest,
  DeleteShippingAddressRequest,
  UpdateDefaultShippingAddressRequest,
  UpdateShippingAddressRequest,
} from '~/api/model';
import { ADDRESS_QUERY_KEY } from '~/api/query-keys';
import { addressRepository } from '~/api/repository/address';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * 배송지 목록 조회 훅
 */
// GET /api/market-place/v1/shipping-address
export const useShippingAddresses = () => {
  const { queryKey, queryFn } = ADDRESS_QUERY_KEY.list();

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/**
 * 기본 배송지 조회 훅
 */
// GET /api/market-place/v1/shipping-address/default
export const useDefaultShippingAddress = () => {
  const { queryKey, queryFn } = ADDRESS_QUERY_KEY.default();

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * 배송지 등록 mutation
 */
// POST /api/market-place/v2/shipping-address
export const useRegisterShippingAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateShippingAddressRequest) =>
      addressRepository.createShippingAddress(data),
    onSuccess: () => {
      void queryClient.invalidateQueries(ADDRESS_QUERY_KEY.all);
    },
  });
};

/**
 * 배송지 수정 mutation
 */
// PUT /api/market-place/v1/shipping-address
export const useUpdateShippingAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateShippingAddressRequest) =>
      addressRepository.updateShippingAddress(data),
    onSuccess: () => {
      void queryClient.invalidateQueries(ADDRESS_QUERY_KEY.all);
    },
  });
};

/**
 * 배송지 삭제 mutation
 */
// DELETE /api/market-place/v1/shipping-address
export const useDeleteShippingAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeleteShippingAddressRequest) =>
      addressRepository.deleteShippingAddress(data),
    onSuccess: () => {
      void queryClient.invalidateQueries(ADDRESS_QUERY_KEY.all);
    },
  });
};

/**
 * 기본 배송지 변경 mutation
 */
// PATCH /api/market-place/v1/shipping-address/default-change
export const useChangeDefaultShippingAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateDefaultShippingAddressRequest) =>
      addressRepository.changeDefaultShippingAddress(data),
    onSuccess: () => {
      void queryClient.invalidateQueries(ADDRESS_QUERY_KEY.all);
    },
  });
};
